import { MouseEvent, useCallback, useState } from 'react';
import { Page } from '@/api';

export const useGdprPageSelection = (pages: Page[] | null) => {
  const [selectedPageIndexes, setSelectedPageIndexes] = useState<number[]>([]);
  // Anchor for shift-click range selection. Set by a plain click or ctrl/meta click.
  // Shift-click reads it but doesn't move it, so successive shift-clicks keep
  // adjusting the same range relative to the original anchor.
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null);

  // Convert selected indexes to page IDs
  const selectedPageIds = selectedPageIndexes
    .map((index) => pages?.[index]?.id)
    .filter((id): id is string => Boolean(id));

  const selectSinglePage = useCallback((itemIndex: number): void => {
    setAnchorIndex(itemIndex);
    setSelectedPageIndexes((prev) => {
      // Avoid redundant selection of the same page
      if (prev.length === 1 && prev[0] === itemIndex) {
        return prev;
      }
      return [itemIndex];
    });
  }, []);

  const toggleMultiplePage = useCallback((itemIndex: number): void => {
    setAnchorIndex(itemIndex);
    setSelectedPageIndexes((prev) => {
      // Avoid deselecting single page when toggling
      if (prev.length === 1 && prev[0] === itemIndex) {
        return prev;
      }

      if (prev.includes(itemIndex)) {
        // Remove if already selected
        return prev.filter((item) => item !== itemIndex);
      } else {
        // Add if not selected
        return [...prev, itemIndex];
      }
    });
  }, []);

  const selectRange = useCallback(
    (itemIndex: number): void => {
      // Anchor → clicked index, inclusive. Replaces the current selection.
      // Falls back to single-select if there's no anchor yet (first click was shift+click).
      const start = anchorIndex ?? itemIndex;
      const lo = Math.min(start, itemIndex);
      const hi = Math.max(start, itemIndex);
      const range: number[] = [];
      for (let i = lo; i <= hi; i++) range.push(i);
      setSelectedPageIndexes(range);
      // Don't move the anchor — keeps successive shift+clicks expanding/shrinking
      // relative to the same starting page (standard file-explorer behavior).
    },
    [anchorIndex],
  );

  const handleSelectPage = useCallback(
    (event: MouseEvent<HTMLDivElement>, itemIndex: number): void => {
      if (event.shiftKey) {
        // shift + click → select range from anchor to clicked page
        selectRange(itemIndex);
      } else if (event.metaKey || event.ctrlKey) {
        // meta/ctrl + click → toggle individual page
        toggleMultiplePage(itemIndex);
      } else {
        // click → single selection
        selectSinglePage(itemIndex);
      }
    },
    [selectRange, toggleMultiplePage, selectSinglePage],
  );

  const isPageSelected = useCallback(
    (index: number): boolean => {
      return selectedPageIndexes.includes(index);
    },
    [selectedPageIndexes],
  );

  const clearSelection = useCallback(() => {
    setSelectedPageIndexes([]);
    setAnchorIndex(null);
  }, []);

  return {
    selectedPageIndexes,
    selectedPageIds,
    handleSelectPage,
    isPageSelected,
    clearSelection,
    hasSelection: selectedPageIndexes.length > 0,
  };
};
